import {
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select as ChakraSelect,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { MdAddCircleOutline } from "react-icons/md";
import Select, { components, GroupBase, MenuListProps } from "react-select";

import { Button, LoadingIndicator, useToast } from "../../../components";
import useDebounce from "../../../hooks/useDebounce";
import useSelectTheme from "../../../hooks/useSelectTheme";
import {
  PositionListItemFragment,
  useSetMembershipsMutation,
} from "../../graphql";
import useCurrentUser from "../../hooks/useCurrentUser";
import { usePositionsPagination } from "../../hooks/usePositionsPagination";

interface AddMembershipButtonProps {
  userIds: string[];
  filterPositionIds?: string[];
}

type PositionOption = {
  label: string;
  value: string;
};

type PositionSelectData = {
  loadMorePositions: () => void;
  hasMoreData: boolean;
  isLoadingMore: boolean;
  loading: boolean;
};

const CustomPositionMenuList: React.FC<
  MenuListProps<PositionOption, false, GroupBase<PositionOption>> & {
    selectProps: { data: PositionSelectData };
  }
> = (props) => {
  const { data } = props.selectProps;

  const handleScrollCapture = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    // Trigger load more when user has scrolled 90% of the way down
    if (
      scrollPercentage > 0.9 &&
      data.hasMoreData &&
      !data.isLoadingMore &&
      !data.loading
    ) {
      data.loadMorePositions();
    }
  };

  return (
    <components.MenuList
      {...props}
      innerProps={{ onScrollCapture: handleScrollCapture }}
    >
      <>
        {props.children}
        {data.isLoadingMore && (
          <Flex p="4" width="100%" justifyContent="center">
            <LoadingIndicator />
          </Flex>
        )}
      </>
    </components.MenuList>
  );
};

const AddMembershipButton: React.FC<AddMembershipButtonProps> = ({
  userIds,
  filterPositionIds,
}) => {
  const currentUser = useCurrentUser();
  const toast = useToast();
  const [theme, styles] = useSelectTheme({
    container: (provided: Record<string, any>) => {
      return { ...provided, flex: 1 };
    },
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [roleId, setRoleId] = useState<string>(
    currentUser.organization.positionRoles[0]?.id
  );
  const [positionId, setPositionId] = useState<string>();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 250);

  const { positions, loading, loadMorePositions, hasMoreData, isLoadingMore } =
    usePositionsPagination({
      searchTerm: debouncedSearchTerm,
      limit: 50,
      onError: (err: Error) => {
        toast({
          title: "Error",
          description: err.message,
          status: "error",
        });
      },
    });

  const position = positions.find(
    (p: PositionListItemFragment) => p.id === positionId
  );
  const [setMemberships] = useSetMembershipsMutation({
    onCompleted: (data) => {
      if (data.setMemberships) {
        onClose();
        toast({
          title: "Success",
          description: "Added to Hiring Team",
          status: "success",
        });
      }
    },
    onError: (err) => {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
      });
    },
  });

  const onSubmit = (): void => {
    if (!roleId) return;
    if (!positionId) return;
    const memberships = userIds.map((userId) => {
      return {
        userId,
        positionId,
        positionRoleId: roleId,
      };
    });
    setMemberships({ variables: { users: memberships } });
  };

  const enabled =
    currentUser.userRole?.canManageHiringTeam ||
    currentUser.memberships.some((m) => m.role.canManageHiringTeam);

  const allPositions = currentUser.userRole?.canManageHiringTeam
    ? positions
    : currentUser.memberships
        .filter((m) => {
          return m.role.canManageHiringTeam;
        })
        .map((m) => m.position);

  const filteredPositions = allPositions.filter(
    (p: any) => !filterPositionIds?.includes(p.id)
  );

  return (
    <>
      <Button
        size="xs"
        variant="outline"
        leftIcon={<MdAddCircleOutline />}
        isDisabled={userIds.length < 1 || !enabled}
        onClick={onOpen}
      >
        Add to hiring team
      </Button>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>Add to hiring team</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing="8">
                <FormControl
                  id="membership-position-select"
                  data-testid="position-select"
                >
                  <FormLabel>Position</FormLabel>
                  <Select
                    {...({
                      theme,
                      styles,
                      isClearable: true,
                      placeholder: "Select a Position",
                      onInputChange: setSearchTerm,
                      isLoading: loading,
                      value: {
                        label: position?.displayTitle,
                        value: positionId,
                      },
                      options: filteredPositions.map((p: any) => ({
                        label: p.displayTitle,
                        value: p.id,
                      })),
                      onChange: (option: any) =>
                        setPositionId(
                          (option as { value: string } | undefined)?.value
                        ),
                      components: {
                        MenuList: CustomPositionMenuList as any,
                      },
                      data: {
                        loadMorePositions,
                        hasMoreData,
                        isLoadingMore,
                        loading,
                      },
                    } as any)}
                  />
                </FormControl>
                <FormControl id="membership-role-select" mt="2">
                  <FormLabel>Role</FormLabel>
                  <ChakraSelect
                    data-testid="membership-role-select"
                    value={roleId}
                    isDisabled={!positionId}
                    onChange={(e) => setRoleId(e.target.value)}
                  >
                    {currentUser.organization.positionRoles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.formattedName}
                      </option>
                    ))}
                  </ChakraSelect>
                  <FormHelperText>
                    {
                      currentUser.organization.positionRoles.find(
                        (r) => r.id === roleId
                      )?.formattedPermissions
                    }
                  </FormHelperText>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={onSubmit} isDisabled={!roleId || !positionId}>
                Submit
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </>
  );
};

export default AddMembershipButton;
